/**
 * The keys used for tagging primitive types.
 * These keys should only be used by the type system and not at runtime.
 */

export const $tag = Symbol('$tag');

const $value = Symbol('$value');
const $$$tags = Symbol('$$$tags');

export type ValueKey = typeof $value;
export type TagsKey = typeof $$$tags;

type Token = string;
type Datum = string | number | boolean;
type Flags = Record<Token, true>;

/**
 * At runtime, this is a primitive
 */
export type TaggedValue<
  Tag extends Token,
  Value extends Datum,
  Tags extends Flags = Flags,
> = Value & {
  readonly [$value]: Value;

  readonly [$$$tags]: Tags &
    // only include the tag if it's a string literal
    (string extends Tag ? { ''?: true } : { [K in Tag]: true });
};

export function tag<
  Tag extends Token,
  Value extends Datum | TaggedValue<Token, Datum>,
>(_tag: Tag, v: Value) {
  return v as TaggedValue<
    Tag,
    Value extends TaggedValue<Token, Datum> ? Value[ValueKey] : Value
  >;
}

/**
 * Noop that converts the type of a tagged enum back to its underlying primitive type.
 * This is useful for comparisons across enums or interpolated string types.
 */
export function untag<T extends TaggedValue<Token, Datum>>(v: T) {
  return v as T[ValueKey];
}

export type TaggedValueExtension<
  Tag extends Token,
  T extends TaggedValue<Token, Datum>,
> = TaggedValue<Tag, T[ValueKey]> & Pick<T, TagsKey>;

export function tagExtension<
  Tag extends Token,
  Value extends Datum,
  T extends TaggedValue<Token, Value>,
>(_tag: Tag, v: Value) {
  return v as TaggedValueExtension<Tag, T>;
}
